import React, { createContext, ReactNode, useContext } from "react";
import type {
  DateFormatApi,
  ForecastHorizonApi,
  NumberFormatApi,
} from "../api/models/userPreferenceDefaults";
import { useUserPreferenceDefaults } from "../hooks/useUserPreferenceDefaults";

interface PreferencesContextType {
  dateFormat: DateFormatApi;
  numberFormat: NumberFormatApi;
  forecastHorizon: ForecastHorizonApi;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(
  undefined,
);

// Global variable to store current preferences (for non-React code)
let globalPreferences: PreferencesContextType = {
  dateFormat: "DD/MM/YYYY",
  numberFormat: "comma-dot",
  forecastHorizon: "90-days",
};

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { prefs } = useUserPreferenceDefaults();

  // Update global preferences whenever they change
  React.useEffect(() => {
    globalPreferences = {
      dateFormat: prefs.dateFormat,
      numberFormat: prefs.numberFormat,
      forecastHorizon: prefs.forecastHorizon,
    };
  }, [prefs.dateFormat, prefs.numberFormat, prefs.forecastHorizon]);

  return (
    <PreferencesContext.Provider
      value={{
        dateFormat: prefs.dateFormat,
        numberFormat: prefs.numberFormat,
        forecastHorizon: prefs.forecastHorizon,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
}

// Export function to get preferences from non-React code
export function getGlobalPreferences(): PreferencesContextType {
  return globalPreferences;
}
